import { useMutation } from '@tanstack/react-query'
import api from '@/services/api'

interface ViatgerInput {
    _guestId?: string // ID del guest existent (per actualitzacions)
    nom: string
    cognom1: string
    cognom2?: string
    email?: string
    telefon?: string
    mobil?: string
    // Opcionals: un menor de 14 no en porta (RD 933/2021). Si són buits, no s'envien.
    tipus_document?: string
    num_document?: string
    nacionalitat: string
    data_naixement: string
    sexe?: string
    adreca?: string
    ciutat?: string
    provincia?: string
    codi_postal?: string
    pais?: string
    empresa?: string
    titol?: string
    peticions_especials?: string
    es_titular?: boolean
    consentiment_rgpd: boolean
    consentiment_marketing?: boolean
    signature?: string
    address_raw?: string
    camp_suport?: string
    data_caducitat_document?: string
    relacio_parentesc?: string
}

interface FormInput {
    viatgers: ViatgerInput[]
}

/** Treu `tipus_document` i `num_document` si són buits: la clau no s'envia */
function withoutEmptyDocument<T extends Pick<ViatgerInput, 'tipus_document' | 'num_document'>>(viatger: T): T {
    const out = { ...viatger }
    if (!out.tipus_document?.trim()) delete out.tipus_document
    if (!out.num_document?.trim()) delete out.num_document
    return out
}

/**
 * Transforma los datos del formulario frontend al formato esperado por el backend.
 * - Combina cognom1 + cognom2 → cognoms
 * - Elimina campos que el backend no espera
 * - No envía tipo/número de documento vacíos
 */
function transformFormData(data: FormInput) {
    return {
        viatgers: data.viatgers.map((viatger) => {
            // Combinar apellidos: cognom1 + cognom2 → cognoms
            const cognoms = [viatger.cognom1, viatger.cognom2]
                .filter(Boolean)
                .join(' ')
                .trim()

            // Crear objeto sin cognom1/cognom2
            const { cognom1, cognom2, ...rest } = withoutEmptyDocument(viatger)

            return {
                ...rest,
                cognoms,
            }
        }),
    }
}

export function useSubmitFormulari(token: string | undefined) {
    return useMutation({
        mutationFn: async (data: FormInput) => {
            if (!token) throw new Error('Token is required')
            
            // Separar viatgers nous dels que s'han d'actualitzar
            const newViatgers = data.viatgers.filter(v => !v._guestId)
            const updateViatgers = data.viatgers.filter(v => v._guestId)
            
            const results: any[] = []
            
            // 1. Actualitzar viatgers existents (PATCH)
            for (const viatger of updateViatgers) {
                const { _guestId, cognom1, cognom2, ...rest } = withoutEmptyDocument(viatger)
                const cognoms = [cognom1, cognom2].filter(Boolean).join(' ').trim()
                
                const response = await api.patch(`/formulari/${token}/guest/${_guestId}`, {
                    ...rest,
                    cognoms,
                })
                results.push({ type: 'update', data: response.data })
            }
            
            // 2. Crear viatgers nous (POST)
            if (newViatgers.length > 0) {
                const transformedData = transformFormData({ viatgers: newViatgers })
                const response = await api.post(`/formulari/${token}`, transformedData)
                results.push({ type: 'create', data: response.data })
            }
            
            return {
                success: true,
                updated: updateViatgers.length,
                created: newViatgers.length,
                results,
            }
        },
    })
}
